import { getAuth } from 'firebase/auth';
import { AnonymousAuthService } from '../utils/anonymousAuth';

async function authHeaders(): Promise<Record<string, string>> {
	let user = getAuth().currentUser;
	console.log('[authHeaders] Initial user:', user ? user.uid : 'NULL');

	if (!user) {
		try {
			console.log('[authHeaders] Attempting anonymous auth...');
			await AnonymousAuthService.getInstance().ensureAuthenticated();
			user = getAuth().currentUser;
			console.log('[authHeaders] After anonymous auth, user:', user ? user.uid : 'STILL NULL');
		} catch (e: any) {
			console.error('[authHeaders] Anonymous auth FAILED:', e?.message || e);
			// No session; return no Authorization header so backend can respond 401
		}
	}

	const token = user ? await user.getIdToken(true) : '';
	console.log('[authHeaders] Token length:', token.length, 'chars');
	if (token) {
		console.log('[authHeaders] Token preview:', token.substring(0, 50) + '...');
	} else {
		console.warn('[authHeaders] No token generated - will return empty Authorization header');
	}

	return {
		'Content-Type': 'application/json',
		...(token ? { Authorization: `Bearer ${token}` } : {})
	};
}

function apiBase(): string {
  // Prefer explicit override if provided
  const override = (process.env.REACT_APP_API_BASE || '').trim();
  if (override) return override.replace(/\/$/, '');
  try {
    const { hostname, port } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    // When using CRA dev server on 3000, route API to Hosting emulator on 5000
    if (isLocal && (port === '3000' || port === '5173' || port === '8080')) return 'http://127.0.0.1:5000';
  } catch {}
  return '';
}

function functionsBase(): string {
  // Prefer explicit override if provided
  const override = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  if (override) return override.replace(/\/$/, '');
  // Return empty - we'll use cloudRunUrl() for direct Cloud Run access
  return '';
}

// Cloud Run URL for 2nd gen functions
// Format: https://{servicename}-{projecthash}-{region}.a.run.app
const CLOUD_RUN_PROJECT_HASH = '3fdbiliy3a';
const CLOUD_RUN_REGION = 'uc'; // us-central1

function cloudRunUrl(serviceName: string): string {
  return `https://${serviceName}-${CLOUD_RUN_PROJECT_HASH}-${CLOUD_RUN_REGION}.a.run.app`;
}

// Map API paths to Cloud Run service names (lowercase)
function mapApiPathToCloudRunService(path: string): string {
  switch (path) {
    case '/api/payments/create-customer': return 'createcustomer';
    case '/api/payments/connected-account-status': return 'getconnectedaccountstatus';
    case '/api/finconn/session': return 'createfinancialconnectionssession';
    case '/api/finconn/attach': return 'attachfinancialconnectionsaccount';
    case '/api/payments/setup-intent': return 'setupintent';
    case '/api/payments/payment-intent': return 'paymentintent';
    case '/api/payments/payout': return 'createpayout';
    case '/api/payments/methods': return 'listpaymentmethods';
    case '/api/payments/banks': return 'listbankaccounts';
    case '/api/payments/detach': return 'detachpaymentmethod';
    case '/api/payments/default': return 'setdefaultpaymentmethod';
    case '/api/payments/workflow-payment-intent': return 'createworkflowpaymentintent';
    case '/api/payments/health-check': return 'stripehealthcheck';
    case '/api/payments/confirm-credit': return 'confirmandcreditpayment';
    default: return '';
  }
}

/**
 * Safety guard: Prevents accidental use of live Stripe keys on localhost.
 * Even if .env.local is misconfigured with a pk_live_ key, this guard
 * blocks payment operations to prevent real charges during development.
 */
let stripeKeyValidated = false;
function validateStripeKeyForEnvironment(): void {
  if (stripeKeyValidated) return;
  stripeKeyValidated = true;

  try {
    const { hostname } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    if (!isLocal) return;

    const pubKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    if (pubKey.startsWith('pk_live_')) {
      console.error(
        '[Stripe Safety] BLOCKED: Live Stripe key (pk_live_) detected on localhost. ' +
        'This would charge real money. Use a pk_test_ key for local development. ' +
        'Update REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env.local file.'
      );
      throw new Error(
        'Stripe safety: Live key detected on localhost. Use pk_test_ for development.'
      );
    }
  } catch (e: any) {
    if (e?.message?.includes('Stripe safety')) throw e;
    // Ignore other errors (e.g., window not available)
  }
}

async function apiFetchJSON<T = any>(path: string, init: RequestInit): Promise<{ res: Response; data: T | { error: string } }> {
  // Safety check: block live Stripe keys on localhost
  if (path.startsWith('/api/payments/')) {
    validateStripeKeyForEnvironment();
  }

  // For 2nd gen Cloud Functions (Cloud Run), call the Cloud Run URL directly
  // This bypasses Firebase Hosting rewrites which don't work with 2nd gen functions
  const serviceName = mapApiPathToCloudRunService(path);

  // Try Cloud Run directly first (most reliable for 2nd gen functions)
  if (serviceName) {
    const cloudRunEndpoint = cloudRunUrl(serviceName);
    console.log(`[apiFetchJSON] Calling Cloud Run directly: ${cloudRunEndpoint}`);
    try {
      const res = await fetch(cloudRunEndpoint, init);
      const text = await res.text().catch(() => '');
      try {
        const json = JSON.parse(text);
        return { res, data: json } as any;
      } catch {
        return { res, data: { error: text || `HTTP ${res.status}` } } as any;
      }
    } catch (e: any) {
      console.error(`[apiFetchJSON] Cloud Run fetch failed:`, e?.message || e);
      // Fall through to try same-origin
    }
  }

  // Fallback: Try same-origin or REACT_APP_API_BASE (for local dev or if Cloud Run fails)
  const firstUrl = `${apiBase()}${path}`;
  console.log(`[apiFetchJSON] Fallback to same-origin: ${firstUrl}`);
  let res = await fetch(firstUrl, init).catch(() => undefined as any);

  if (!res) return { res: new Response('', { status: 0 }), data: { error: 'Network error' } } as any;

  const text = await res.text().catch(() => '');
  try {
    const json = JSON.parse(text);
    return { res, data: json } as any;
  } catch {
    return { res, data: { error: text || `HTTP ${res.status}` } } as any;
  }
}

async function safeJson<T = any>(res: Response): Promise<T | { error: string }> {
	const text = await res.text().catch(() => '');
	try { return JSON.parse(text) as T; } catch { return { error: text || `HTTP ${res.status}` } as any; }
}

export type ConnectedAccountStatus = {
	status: 'not_created' | 'pending' | 'active';
	accountId: string | null;
	payoutsEnabled?: boolean;
	chargesEnabled?: boolean;
	bankLast4?: string | null;
	bankName?: string | null;
	availablePayoutUsd?: number;
};

export const stripeApi = {
	createCustomer: async (): Promise<{ customerId?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/create-customer', { method: 'POST', headers: await authHeaders() });
		if (!res.ok) return data as any;
		return data as any;
	},
	getConnectedAccountStatus: async (): Promise<ConnectedAccountStatus & { error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/connected-account-status', {
			method: 'GET',
			headers: await authHeaders()
		});
		if (!res.ok) return data as any;
		return data as any;
	},
	createFinConnSession: async (): Promise<{ clientSecret?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/finconn/session', { method: 'POST', headers: await authHeaders() });
		if (!res.ok) return data as any;
		return data as any;
	},
	attachFinConnAccount: async (accountId: string): Promise<{ status?: string; clientSecret?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/finconn/attach', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ accountId }) });
		if (!res.ok) return data as any;
		return data as any;
	},
	/**
	 * Idempotency attempt id (2026-08-18 audit): the server folds this into
	 * the Stripe idempotency key, so a double-click or retried network call
	 * can never charge (or hire) twice. Contract: one id per ATTEMPT — keep
	 * it across error retries, mint a new one when the amount/plan/card
	 * changes or after a success.
	 */
	newPaymentAttemptId: (): string =>
		typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
			? crypto.randomUUID()
			: `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`,
	setupIntent: async (type: 'card' | 'us_bank_account' = 'card'): Promise<{ clientSecret?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/setup-intent', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ type }) });
		if (!res.ok) return data as any;
		return data as any;
	},
	paymentIntent: async (body: { amountUsd?: number; tokens?: number; operation?: 'deposit' | 'buy_tokens'; paymentMethodId?: string; baseFee?: number; accountId?: string; sharedPaymentMethodId?: string; attemptId?: string }): Promise<{ clientSecret?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/payment-intent', { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body) });
		if (!res.ok) return data as any;
		return data as any;
	},
	// createPayout removed (2026-08-18 audit): the legacy direct-payout lane
	// is 410-gated server-side — withdrawals go through requestWalletWithdrawal.
	listMethods: async (): Promise<{ methods: { id: string; brand?: string; last4?: string; exp_month?: number; exp_year?: number; name?: string; is_default?: boolean }[]; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/methods', { headers: await authHeaders() });
		if (!res.ok) return data as any;
		return data as any;
	},
	// The endpoint existed server-side all along (customers.update
	// invoice_settings.default_payment_method) — this client was the missing
	// half (2026-08-18 audit): without it no surface could change the default
	// the top-up/hire modals preselect.
	setDefaultPaymentMethod: async (paymentMethodId: string): Promise<{ ok?: boolean; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/default', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ paymentMethodId }) });
		if (!res.ok) return data as any;
		return data as any;
	},
	listBanks: async (): Promise<{ methods: { id: string; bank_name?: string; last4?: string; account_type?: string }[]; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/banks', { headers: await authHeaders() });
		if (!res.ok) return data as any;
		return data as any;
	},
	detach: async (paymentMethodId: string): Promise<{ detached?: string; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/detach', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ paymentMethodId }) });
		if (!res.ok) return data as any;
		return data as any;
	},
	setDefault: async (paymentMethodId: string): Promise<{ ok?: boolean; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/default', { method: 'POST', headers: await authHeaders(), body: JSON.stringify({ paymentMethodId }) });
		if (!res.ok) return data as any;
		return data as any;
	},
	healthCheck: async (): Promise<{ mode?: string; accountPrefix?: string; timestamp?: number; ok?: boolean; error?: string }> => {
		const { res, data } = await apiFetchJSON('/api/payments/health-check', { headers: await authHeaders() });
		if (!res.ok) return data as any;
		return data as any;
	},

	/**
	 * Confirm a payment and credit the wallet.
	 * Called after stripe.confirmCardPayment() succeeds as the PRIMARY crediting path.
	 * The webhook serves as a backup. Idempotent — safe to call even if webhook already credited.
	 */
	confirmAndCreditPayment: async (paymentIntentId: string): Promise<{
		credited?: boolean;
		alreadyCredited?: boolean;
		transactionId?: string;
		error?: string;
	}> => {
		const { res, data } = await apiFetchJSON('/api/payments/confirm-credit', {
			method: 'POST',
			headers: await authHeaders(),
			body: JSON.stringify({ paymentIntentId })
		});
		if (!res.ok) return data as any;
		return data as any;
	},

	/**
	 * Create a PaymentIntent for workflow payment gates
	 * Returns clientSecret for Stripe Elements and saved payment methods for one-click pay
	 */
	createWorkflowPaymentIntent: async (params: {
		amountUsd: number;
		targetUid?: string;
		workflowId?: string;
		stepId?: string;
		description?: string;
		allowedMethods?: string[];
		metadata?: Record<string, any>;
		invoiceTitle?: string;
	}): Promise<{
		clientSecret?: string;
		paymentIntentId?: string;
		savedMethods?: {
			cards: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number; isDefault: boolean }>;
			bankAccounts: Array<{ id: string; bankName: string; last4: string; accountType: string; isDefault: boolean }>;
		};
		error?: string;
	}> => {
		const { res, data } = await apiFetchJSON('/api/payments/workflow-payment-intent', {
			method: 'POST',
			headers: await authHeaders(),
			body: JSON.stringify(params)
		});
		if (!res.ok) return data as any;
		return data as any;
	}
};